import { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { userApiService } from '@/shared/services/userApiService';
import { Input } from '@/shared/components/ui';
import { ROUTES } from '@/shared/config';
import { useSafeThemeClasses } from '@/shared/theme';
import { logger } from '@/shared/utils';
import toast from 'react-hot-toast';

interface FormErrors {
	newPassword?: string;
	confirmPassword?: string;
}

function ResetPasswordPage() {
	const theme = useSafeThemeClasses();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') || '';

	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [errors, setErrors] = useState<FormErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		const nextErrors: FormErrors = {};
		if (newPassword.length < 6) {
			nextErrors.newPassword = 'Password must be at least 6 characters long';
		}
		if (confirmPassword !== newPassword) {
			nextErrors.confirmPassword = 'Passwords do not match';
		}
		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors);
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await userApiService.resetPassword(token, newPassword);
			if (response.success) {
				toast.success('Password reset. You can now sign in.');
				navigate(ROUTES.LOGIN);
			} else {
				toast.error(response.error?.message || 'This reset link is invalid or has expired.');
			}
		} catch (err) {
			logger.error('Reset password request failed', 'ResetPasswordPage', { error: err });
			toast.error('This reset link is invalid or has expired.');
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!token) {
		return (
			<div className={`min-h-screen flex items-center justify-center px-4 py-12 ${theme.background}`}>
				<div className="w-full max-w-md">
					<div className={`${theme.card} rounded-2xl p-8 space-y-6 text-center`}>
						<h1 className={`text-2xl font-bold ${theme.textHeading} tracking-tight`}>Invalid reset link</h1>
						<p className={theme.textSecondary}>
							This link is missing its reset token. Request a new one below.
						</p>
						<Link
							to={ROUTES.FORGOT_PASSWORD}
							className={`block w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
						>
							Request a new link
						</Link>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={`min-h-screen flex items-center justify-center px-4 py-12 transition-opacity duration-500 ${theme.background}`}>
			<div className="w-full max-w-md">
				<div className={`${theme.card} rounded-2xl p-8 space-y-6`}>
					<div className="text-center">
						<h1 className={`text-2xl font-bold ${theme.textHeading} tracking-tight`}>Choose a new password</h1>
						<p className={`mt-2 text-sm ${theme.textSecondary}`}>
							Make it at least 6 characters long.
						</p>
					</div>

					<form onSubmit={handleSubmit} className="space-y-5">
						<div className="form-control">
							<Input
								type="password"
								id="newPassword"
								name="newPassword"
								placeholder="New password"
								value={newPassword}
								onChange={(e: ChangeEvent<HTMLInputElement>) => {
									setNewPassword(e.target.value);
									if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: undefined }));
								}}
								error={errors.newPassword}
								required
								className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
							/>
						</div>

						<div className="form-control">
							<Input
								type="password"
								id="confirmPassword"
								name="confirmPassword"
								placeholder="Confirm new password"
								value={confirmPassword}
								onChange={(e: ChangeEvent<HTMLInputElement>) => {
									setConfirmPassword(e.target.value);
									if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
								}}
								error={errors.confirmPassword}
								required
								className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
							/>
						</div>

						<button
							type="submit"
							disabled={isSubmitting}
							className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center ${
								isSubmitting ? `${theme.buttonDisabled} ${theme.buttonTextDisabled}` : `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
							}`}
						>
							{isSubmitting ? 'Resetting...' : 'Reset password'}
						</button>
					</form>
				</div>
			</div>
		</div>
	);
}

export default ResetPasswordPage;
